/* eslint-disable no-use-before-define */
import partition from 'lodash.partition';
import Request from '@bufferapp/buffer-js-request';
import { AppEnvironments } from '~/constants';
import { Service } from '~/post/types.generated';
import transformChannelData from './ChannelDataTransformer';
import AppActionCreators from '../action-creators/AppActionCreators';
import { AttachmentTypes, QueueingTypes } from '../AppConstants';
import AppStore from '../stores/AppStore';
import API from './API';
import { ensureUrlProtocol } from './StringUtils';
import { getCurrentPage } from './TrackingUtils';

import { EditorStateProxy } from '../entities/EditorStateProxy';
import { DraftMethods } from '../entities/Draft';

class APIRequestError extends Error {
  name = 'APIRequestError';
}

const WebAPIUtils = {
  rememberModalView: (modalKey) => {
    const reqSettings = { credentials: 'same-origin' };
    Request.get(`/message/read/${modalKey}`, {}, reqSettings);
  },
  /**
   * Save the drafts to the API.
   *
   * data = {
   *   queueingType: QueueingTypes.X,
   *   customScheduleTime: timestamp || null,
   *   profiles: [profile, …], // All selected profiles
   *   drafts: [draft, …], // All enabled drafts
   * }
   */
  saveDrafts: ({ queueingType, customScheduleTime, profiles, drafts }) => {
    const { updateId } = AppStore.getMetaData();
    const { partiallySavedDraftsProfilesIds } = AppStore.getAppState();
    // Transform each draft's data to match the API's format

    const draftsDataToSave = drafts.map((draft) => ({
      serviceName: draft.service.name,
      formattedData: getFormattedAPIData(draft.service.name, {
        queueingType,
        customScheduleTime: customScheduleTime || draft.scheduledAt,
        serviceProfiles: profiles.filter((profile) => {
          const wasUpdateSavedAlreadyForProfile = (
            partiallySavedDraftsProfilesIds.get(draft.service.name) || []
          ).includes(profile.id);

          return (
            profile.service.name === draft.service.name &&
            !wasUpdateSavedAlreadyForProfile
          );
        }),
        serviceDraft: draft,
        isEditingUpdate: updateId !== null,
        isSavedUpdate: draft.isSaved === true,
      }),
    }));

    // Further split requests to prevent more than 10 profiles per request
    const maxProfilesIdsPerRequest = 10;
    const splitDraftsDataToSave = [].concat(
      ...draftsDataToSave.map((draft) =>
        draft.formattedData.profile_ids.length < maxProfilesIdsPerRequest
          ? [draft]
          : draft.formattedData.profile_ids
              .map((_, i) =>
                i % maxProfilesIdsPerRequest === 0
                  ? draft.formattedData.profile_ids.slice(
                      i,
                      i + maxProfilesIdsPerRequest
                    )
                  : null
              )
              .filter((profilesIdsGroup) => profilesIdsGroup !== null)
              .map((profilesIdsGroup) => ({
                ...draft,
                formattedData: {
                  ...draft.formattedData,
                  profile_ids: profilesIdsGroup,
                },
              }))
      )
    );

    /**
     * Make one query per draft.
     *
     * Transform any unexpected network failure into a resolved object
     * with success=false for consistency with API errors, and in order
     * for Promise.all() to not fail early
     */
    const savePromises = splitDraftsDataToSave.map(
      ({ serviceName, formattedData }) => {
        const updatePost =
          queueingType === QueueingTypes.SAVE ||
          queueingType === QueueingTypes.SAVE_AND_APPROVE;
        const endpoint = updatePost
          ? `updates/${updateId}/update.json`
          : 'updates/create.json';

        return API.post(endpoint, formattedData)
          .catch(() => ({
            success: false,
            message: `Whoops, Buffer's servers couldn't be reached to save the
            update, sorry about that. Would you be up for trying again?`,
          }))
          .then((response) => {
            return Object.assign(response, { serviceName });
          });
      }
    );

    return Promise.all(savePromises).then((responses) => {
      /**
       * Since we're splitting requests to never have more than 10 profiles each,
       * we'll sometimes get multiple (un)successful responses for the same draft:
       * - Unsuccessful responses are merged back into one response per draft.
       * - Successful responses are split in two buckets: "fully" successful responses
       *   on one side (i.e. responses for drafts that were saved to all their selected
       *   profiles), and "partially" successful responses (i.e. responses for drafts
       *   that couldn't be saved to all their selected profiles). A draft that was split
       *   in multiple requests will have all of its responses in one of those buckets,
       *   never in both.
       *   - "Fully" successful responses are then merged back into one response per draft
       *   - And we save the "partially" successful responses are used to
       */
      // eslint-disable-next-line prefer-const
      let [unfilteredSuccessfulResponses, unsuccessfulResponses] = partition(
        responses,
        (response) => response.success
      );

      // eslint-disable-next-line prefer-const
      let [partiallySuccessfulResponses, successfulResponses] = partition(
        unfilteredSuccessfulResponses,
        (successfulResponse) =>
          unsuccessfulResponses.find(
            (unsuccessfulResponse) =>
              unsuccessfulResponse.serviceName ===
              successfulResponse.serviceName
          )
      );

      // If a service's responses span both successfulResponses and unsuccessfulResponses,
      // keep track of successful saves for that service in order to skip them during next save
      AppActionCreators.updatePartiallySavedDraftsProfilesIds(
        partiallySuccessfulResponses.map(({ serviceName, updates }) => ({
          draftId: serviceName,
          profilesIds: updates.map(({ profile_id: profileId }) => profileId), // TODO: issue when mult partials?
        }))
      );

      // Merge "fully" successful responses back together again into one response per service
      successfulResponses = successfulResponses.reduce(
        (mergedSuccessfulResponses, response) => {
          const existingServiceEntry = mergedSuccessfulResponses.find(
            (mergedResponse) =>
              mergedResponse.serviceName === response.serviceName
          );

          if (existingServiceEntry) {
            existingServiceEntry.updates.splice(-1, 0, ...response.updates);
          } else {
            mergedSuccessfulResponses.push(response);
          }

          return mergedSuccessfulResponses;
        },
        []
      );

      // Merge unsuccessful responses back together again into one response per service
      unsuccessfulResponses = unsuccessfulResponses.reduce(
        (mergedUnsuccessfulResponses, response) => {
          const hasExistingServiceEntry = !!mergedUnsuccessfulResponses.find(
            (mergedResponse) =>
              mergedResponse.serviceName === response.serviceName
          );

          if (!hasExistingServiceEntry) {
            mergedUnsuccessfulResponses.push(response);
          }

          return mergedUnsuccessfulResponses;
        },
        []
      );

      return {
        successfulResponses,
        unsuccessfulResponses: overrideAPIErrorResponseMessages({
          unsuccessfulResponses,
          partiallySuccessfulResponses,
          queueingType,
        }),
      };
    });
  },

  approveDrafts: (ids) =>
    Promise.all(
      ids.map((id) =>
        API.get(`updates/${id}/approve.json`).catch(() => ({ success: false }))
      )
    ),

  createNewSubprofile: (profileId, data) => {
    AppActionCreators.createSubprofilePending(profileId);

    return API.post(`profiles/${profileId}/subprofiles/create.json`, data)
      .catch(() => {
        throw new APIRequestError(`Whoops, Buffer's servers couldn't be reached
          to save the board, sorry about that. Would you be up for trying again?`);
      })
      .then((response) => {
        if (
          !Object.prototype.hasOwnProperty.call(response, 'success') ||
          response.success === false
        ) {
          throw new APIRequestError(`The board couldn't be saved, sorry about that.
            Would you be up for trying again?`);
        } else {
          return {
            profileId: response.subprofile.profile_id,
            id: response.subprofile.id,
            avatar: response.subprofile.avatar,
            name: response.subprofile.name,
            isShared: response.subprofile.is_shared,
          };
        }
      });
  },

  fetchProfileSubprofiles: (profileId) =>
    API.get(`profiles/${profileId}/fetch_subprofiles.json`).then(
      (response) => ({
        subprofiles: response.subprofiles.map(
          // eslint-disable-next-line camelcase
          ({ profile_id, id, avatar, name, is_shared }) => ({
            profileId: profile_id,
            isShared: is_shared,
            id,
            avatar,
            name,
          })
        ),
      })
    ),

  getProfileSlotDataForDateRange: (profileId, startDay, endDay) =>
    API.get(`profiles/${profileId}/schedules/slots.json`, {
      start_day: startDay,
      end_day: endDay,
    }),

  getFacebookDomainOwnershipForProfile: (profileId, url) =>
    API.get(`profiles/${profileId}/facebook_domain_ownership.json`, {
      profile_id: profileId,
      url,
    }),
};

function overrideAPIErrorResponseMessages({
  unsuccessfulResponses,
  partiallySuccessfulResponses,
  queueingType,
}) {
  return unsuccessfulResponses.map((response) => {
    const hasPartiallySuccessfulResponses = !!partiallySuccessfulResponses.find(
      ({ serviceName }) => serviceName === response.serviceName
    );

    if (hasPartiallySuccessfulResponses) {
      const errorMessageVerbQueueingTypeMap = new Map([
        [QueueingTypes.QUEUE, 'queued'],
        [QueueingTypes.NEXT, 'queued'],
        [QueueingTypes.NOW, 'posted'],
        [QueueingTypes.CUSTOM, 'saved'],
        [QueueingTypes.SAVE, 'saved'],
        [QueueingTypes.SAVE_AND_APPROVE, 'saved and approved'],
        [QueueingTypes.ADD_DRAFT, 'saved'],
        [QueueingTypes.NEXT_DRAFT, 'saved'],
        [QueueingTypes.CUSTOM_DRAFT, 'saved'],
      ]);

      response.message = `Some updates were successfully ${errorMessageVerbQueueingTypeMap.get(
        queueingType
      )},
        but some others weren't, here's what happened if you want to try again for those
        that failed:<br/>${response.message}`;
    }

    return response;
  });
}

function getFormattedMediaFields(isEditingUpdate, serviceDraft) {
  const hasImageAttachment =
    Array.isArray(serviceDraft.images) && serviceDraft.images.length > 0;
  const hasVideoAttachment = serviceDraft.video !== null;
  const hasGifAttachment = serviceDraft.gif !== null;
  const hasEnabledMediaAttachment =
    serviceDraft.enabledAttachmentType === AttachmentTypes.MEDIA &&
    (hasImageAttachment || hasVideoAttachment || hasGifAttachment);
  const hasEnabledLinkAttachment =
    serviceDraft.enabledAttachmentType === AttachmentTypes.LINK &&
    serviceDraft.link !== null;

  // Default to non-set values on create, and nulls on update in order to
  // override those fields in the API
  let formattedMediaFields = isEditingUpdate
    ? {
        media: null,
        extra_media: null,
      }
    : {};

  const getFormattedImageFields = (image) => ({
    progress: 100,
    uploaded: true,
    photo: image.url,
    picture: image.url,
    thumbnail: image.url,
    alt_text: image.altText || null,
    source: image.source,
    height: image.height,
    width: image.width,
  });

  if (hasEnabledLinkAttachment) {
    const doesLinkAttachmentHaveThumbnail =
      serviceDraft.link.thumbnail !== null;

    formattedMediaFields = {
      ...formattedMediaFields,
      media: {
        link: serviceDraft.link.url,
        title: serviceDraft.link.title,
        description: serviceDraft.link.description,
      },
    };

    if (doesLinkAttachmentHaveThumbnail) {
      Object.assign(formattedMediaFields.media, {
        preview: serviceDraft.link.thumbnail.url,
        preview_safe: serviceDraft.link.thumbnailHttps,
        picture: serviceDraft.link.thumbnail.url,
        original_preview: serviceDraft.link.thumbnail.originalUrl,
      });
    }
  } else if (hasEnabledMediaAttachment) {
    if (hasImageAttachment) {
      const [firstImage, ...otherImages] = serviceDraft.images;

      formattedMediaFields = {
        ...formattedMediaFields,
        media: getFormattedImageFields(firstImage),
      };

      // If several images were attached, attach remaining ones to the extra_media field
      if (otherImages.length > 0) {
        formattedMediaFields.extra_media = {};
        otherImages.forEach((image, index) => {
          formattedMediaFields.extra_media[index] =
            getFormattedImageFields(image);
        });
      }
    } else if (hasGifAttachment) {
      const gif = serviceDraft.gif.url;

      formattedMediaFields = {
        ...formattedMediaFields,
        media: {
          progress: 100,
          uploaded: true,
          photo: gif,
          picture: gif,
          thumbnail: gif,
        },
      };
    } else if (hasVideoAttachment) {
      const { video } = serviceDraft;

      formattedMediaFields = {
        ...formattedMediaFields,
        media: {
          progress: 100,
          uploaded: true,
          uploading_type: 'video',
          video: {
            title: video.name,
            id: video.uploadId,
            details: {
              location: video.originalUrl,
              transcoded_location: video.url,
              file_size: video.size,
              duration: video.duration,
              duration_millis: video.durationMs,
              width: video.width,
              height: video.height,
            },
            thumb_offset: video.thumbOffset,
            thumbnails: video.availableThumbnails,
          },
          thumbnail: video.thumbnail,
        },
      };
    }
  }

  return formattedMediaFields;
}

function getFormattedThreadData(serviceDraft, isEditingUpdate) {
  return serviceDraft.thread.map((threadedDraft) => {
    const threadedUpdate = {
      text: threadedDraft.text,
      ...getFormattedMediaFields(isEditingUpdate, threadedDraft),
    };

    const hasThreadedRetweetAttachment =
      threadedDraft.enabledAttachmentType === AttachmentTypes.RETWEET &&
      threadedDraft.retweet !== null;
    if (hasThreadedRetweetAttachment) {
      const { retweet } = threadedDraft;

      Object.assign(threadedUpdate, {
        is_native_retweet: false,
        retweeted_tweet_id: retweet.tweetId,
        retweet: {
          user_id: retweet.userId,
          tweet_id: retweet.tweetId,
          user_name: retweet.userName,
          display_name: retweet.userDisplayName,
          url: retweet.tweetUrl,
          avatar_http: retweet.avatarUrl,
          avatar_https: retweet.avatarUrl,
          comment: threadedUpdate.text,
        },
      });
    }

    return threadedUpdate;
  });
}

/**
 * Transform data to the format the API consumes.
 *
 * unformattedData = {
 *   queueingType: QueueingTypes.X,
 *   customScheduleTime: timestamp || null,
 *   serviceProfiles: [profile, …], // Selected profiles for that service
 *   serviceDraft: draft, // Draft for that service
 *   isEditingUpdate: boolean,
 * }
 */
function getFormattedAPIData(serviceName, unformattedData) {
  const appMetaData = AppStore.getMetaData();
  const { serviceDraft, isEditingUpdate, isSavedUpdate } = unformattedData;
  const serviceDraftText = EditorStateProxy.getPlainText(
    serviceDraft.editorState
  );
  const serviceProfilesIds = unformattedData.serviceProfiles.map(
    (profile) => profile.id
  );
  const serviceSelectedSubprofiles = unformattedData.serviceProfiles.map(
    (profile) => profile.selectedSubprofileId
  );
  const hasEnabledLinkAttachment =
    serviceDraft.enabledAttachmentType === AttachmentTypes.LINK &&
    serviceDraft.link !== null;
  const hasEnabledRetweetAttachment =
    serviceDraft.enabledAttachmentType === AttachmentTypes.RETWEET &&
    serviceDraft.retweet !== null;
  const hadEnabledRetweetAttachment =
    appMetaData.retweetData !== null && !hasEnabledRetweetAttachment;
  const shouldShortenLinks = !DraftMethods.hasUnshortenedUrls(serviceDraft);
  const isTwitterThread =
    serviceDraft.updateType === 'thread' &&
    serviceDraft.service.isTwitter() &&
    Array.isArray(serviceDraft.thread);

  const getConditionalFields = () => {
    let conditionalFields = {};

    const didUserSetScheduledAtBeforeSession =
      appMetaData.didUserSetScheduledAt;
    const didUserChangeScheduledAtDuringSession =
      appMetaData.scheduledAt !== unformattedData.customScheduleTime;

    // Only set scheduled_at and pinned fields when an update is created with a
    // custom time, or when it's edited with a custom time and this time wasn't
    // automatically generated by our queueing engine (i.e. the time was user-set)
    if (
      didUserSetScheduledAtBeforeSession ||
      didUserChangeScheduledAtDuringSession
    ) {
      conditionalFields.scheduled_at = unformattedData.customScheduleTime;
      conditionalFields.pinned = !!serviceDraft.isPinnedToSlot;
    }

    if (serviceDraft.service.hasSubprofiles) {
      if (isEditingUpdate && appMetaData.editMode) {
        // The API expects subprofile_id (a single id) when editing updates
        const [firstSubprofileId] = serviceSelectedSubprofiles;
        conditionalFields.subprofile_id = firstSubprofileId;
      } else {
        conditionalFields.subprofile_ids = serviceSelectedSubprofiles;
      }
    }

    if (appMetaData.serviceUpdateId) {
      conditionalFields.service_update_id = appMetaData.serviceUpdateId;
    }

    if (serviceDraft.ideaId) {
      conditionalFields.ideaId = serviceDraft.ideaId;
    }

    if (serviceDraft.service.canHaveSourceUrl) {
      const { sourceLink } = serviceDraft;

      if (sourceLink !== null) {
        const sourceUrl = ensureUrlProtocol(sourceLink.url);
        conditionalFields.source_url = sourceUrl;
      }
    }

    if (serviceDraft.service.canHaveLocation) {
      const { locationId, locationName } = serviceDraft;
      conditionalFields.service_geolocation_id = locationId;
      conditionalFields.service_geolocation_name = locationName;
    }

    if (serviceDraft.updateType) {
      conditionalFields.update_type = serviceDraft.updateType;
    }

    const { images } = serviceDraft;
    const userTags = images && images[0] && images[0].userTags;
    if (serviceDraft.service.canHaveUserTags) {
      conditionalFields.service_user_tags =
        // Ensuring we send `null` here so it removes the tags when all are removed
        userTags && userTags.length ? userTags : null;
    }

    const { campaignId } = serviceDraft;
    conditionalFields.campaign_id = campaignId;

    if (hasEnabledRetweetAttachment) {
      const { retweet } = serviceDraft;

      conditionalFields = Object.assign(conditionalFields, {
        is_native_retweet: !isTwitterThread,
        retweeted_tweet_id: retweet.tweetId,
        retweet: {
          user_id: retweet.userId,
          tweet_id: retweet.tweetId,
          user_name: retweet.userName,
          display_name: retweet.userDisplayName,
          url: retweet.tweetUrl,
          avatar_http: retweet.avatarUrl,
          avatar_https: retweet.avatarUrl,
          comment: serviceDraftText,
        },
      });
      // The API expects retweet to be null when editing an update and removing its retweet attachment
    } else if (isEditingUpdate && hadEnabledRetweetAttachment) {
      conditionalFields = Object.assign(conditionalFields, {
        retweet: null,
      });
    }

    if (serviceDraft.service.isInstagram()) {
      conditionalFields = Object.assign(conditionalFields, {
        comment_enabled:
          serviceDraft.commentText && serviceDraft.commentText.trim() !== '',
        comment_text: serviceDraft.commentText,
        link: serviceDraft.shopgridLink,
      });
    }

    if (serviceDraft.service.isPinterest()) {
      conditionalFields = Object.assign(conditionalFields, {
        title: serviceDraft.title,
      });
    }

    if (serviceDraft.thread) {
      conditionalFields.thread = getFormattedThreadData(
        serviceDraft,
        isEditingUpdate
      );
    }

    // If Thread was deleted, we want to ensure we pass null here so it's preserved in DB
    if (Array.isArray(AppStore.getMetaData().thread) && !serviceDraft.thread) {
      conditionalFields.thread = null;
    }

    // If UpdateType was unset, we want to ensure we pass null here so it's preserved in DB
    if (AppStore.getMetaData().updateType && !serviceDraft.updateType) {
      conditionalFields.update_type = null;
    }

    return conditionalFields;
  };
  const { tabId, emptySlotMode } = AppStore.getMetaData();

  const annotations =
    serviceName === Service.Linkedin
      ? EditorStateProxy.getAllLinkedinAnnotations(serviceDraft.editorState)
      : [];

  return {
    now: unformattedData.queueingType === QueueingTypes.NOW,
    top:
      unformattedData.queueingType === QueueingTypes.NEXT ||
      unformattedData.queueingType === QueueingTypes.NEXT_DRAFT,
    is_draft:
      unformattedData.queueingType === QueueingTypes.ADD_DRAFT ||
      unformattedData.queueingType === QueueingTypes.NEXT_DRAFT ||
      unformattedData.queueingType === QueueingTypes.CUSTOM_DRAFT,
    shorten: shouldShortenLinks,
    // When editing updates, the API expects only the text field to be used (not fb_text)
    text: serviceDraftText,
    fb_text:
      serviceName === 'facebook' && !isSavedUpdate ? serviceDraftText : '',
    entities:
      serviceName === 'facebook'
        ? EditorStateProxy.getFacebookAutocompleteEntities(
            serviceDraft.editorState
          )
        : null,
    annotations,
    profile_ids: serviceProfilesIds,
    attachment: hasEnabledLinkAttachment,
    via:
      appMetaData.appEnvironment === AppEnvironments.EXTENSION
        ? 'bookmarklet'
        : null,
    source: appMetaData.browser,
    version: appMetaData.extensionVersion,
    created_source: getCurrentPage(tabId, emptySlotMode),
    channel_data: transformChannelData(
      serviceDraft.channelData,
      serviceDraft.updateType
    ),
    ...getConditionalFields(),
    ...getFormattedMediaFields(isEditingUpdate, serviceDraft),
  };
}

export default WebAPIUtils;
